import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
// components
import AppFormInput from "../../../../components/form/AppFormInputs/AppFormInput";
import AppDatePickerCustom from "../../../../components/AppDatePickerCustom";
import EuropcarInsuranceSection from "../EuropcarInsuranceSection";
import EuropcarWhatsIncludedSection from "../EuropcarWhatsIncludedSection";
// helpers
import { isVisibleField } from "../helpers/isVisibleField";
// actions
import { useActions } from "../../../../common/hooks/useActions";
import { europcarActionCreators } from "../europcarModule";

interface EuropcarDriverDetailsFormProps {
  selectedCar: any;
}

export default function EuropcarDriverDetailsForm({ selectedCar }: EuropcarDriverDetailsFormProps) {
  const { t } = useTranslation();

  // states
  const errorField = useSelector((state: any) => state.errorField.errorField);
  const dataForBooking = useSelector((state: any) => state.europcar.dataForBooking);
  const europcarInitialItem = useSelector((state: any) => state.europcar.europcarInitialItem);

  // actions
  const updateFormForBooking = useActions(europcarActionCreators?.updateFormForBookingAction, []);

  // Handle Forms //

  const handleBookingFormInputChange = (e: any, input: any) => {
    switch (input) {
      case "nameForm":
        updateFormForBooking({
          ...dataForBooking,
          nameForm: e.target.value
        });
        break;
      case "firstNameForm":
        updateFormForBooking({
          ...dataForBooking,
          firstNameForm: e.target.value
        });
        break;
      case "lastNameForm":
        updateFormForBooking({
          ...dataForBooking,
          lastNameForm: e.target.value
        });
        break;
      case "emailForm":
        updateFormForBooking({
          ...dataForBooking,
          emailForm: e.target.value
        });
        break;
      case "mobileNumberForm":
        updateFormForBooking({
          ...dataForBooking,
          mobileNumberForm: e.target.value
        });
        break;
      case "commentForm":
        updateFormForBooking({
          ...dataForBooking,
          commentForm: e.target.value
        });
        break;
      case "acceptTerms":
        updateFormForBooking({
          ...dataForBooking,
          acceptTerms: e.target.checked
        });
        break;
      default:
        break;
    }
  };

  const birthdateChange = (date: Date) => {
    updateFormForBooking({
      ...dataForBooking,
      dateOfBirth: date
    });
  };

  // END OF Handle Forms //

  return (
    <Box>
      <Box className="summary-option-title" mb={2}>
        {t("transport.driverVehicleDetailsTitle")}
      </Box>

      <Box sx={{ flexDirection: { xs: "column", sm: "column", md: "row" }, display: "flex", alignItems: "flex-start" }}>
        <Grid container width={{ sm: "100%", md: "50%" }} mt={2}>
          {isVisibleField(europcarInitialItem?.booking_setting?.customer_name) && (
            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {t("transport.guestNameLabel")}{" "}
                  {europcarInitialItem?.booking_setting?.customer_name === "mandatory" && "*"}
                </Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"nameForm"}
                  type="string"
                  value={dataForBooking?.nameForm}
                  onChange={event => handleBookingFormInputChange(event, "nameForm")}
                  error={errorField?.nameForm}
                  dataAttribute="europcar-driver-name-input"
                />
              </Grid>
            </Grid>
          )}
          {isVisibleField(europcarInitialItem?.booking_setting?.first_name) && (
            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {t("transport.driverFirstNameLabel")}{" "}
                  {europcarInitialItem?.booking_setting?.first_name === "mandatory" && "*"}
                </Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"firstNameForm"}
                  type="string"
                  value={dataForBooking?.firstNameForm}
                  onChange={event => handleBookingFormInputChange(event, "firstNameForm")}
                  error={errorField?.firstNameForm}
                  dataAttribute="europcar-driver-first-name-input"
                />
              </Grid>
            </Grid>
          )}
          {isVisibleField(europcarInitialItem?.booking_setting?.last_name) && (
            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {t("transport.driverLastNameLabel")}{" "}
                  {europcarInitialItem?.booking_setting?.last_name === "mandatory" && "*"}
                </Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"lastNameForm"}
                  type="string"
                  value={dataForBooking?.lastNameForm}
                  onChange={event => handleBookingFormInputChange(event, "lastNameForm")}
                  error={errorField?.lastNameForm}
                  dataAttribute="europcar-driver-last-name-input"
                />
              </Grid>
            </Grid>
          )}
          {isVisibleField(europcarInitialItem?.booking_setting?.birthdate) && (
            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {t("transport.formBirthdate")}{" "}
                  {europcarInitialItem?.booking_setting?.birthdate === "mandatory" && "*"}
                </Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppDatePickerCustom
                  selectedDate={dataForBooking?.dateOfBirth}
                  setSelectedDate={date => date != null && birthdateChange(date)}
                  dataAttribute="europcar-driver-birthdate-picker"
                />
              </Grid>
            </Grid>
          )}
          {isVisibleField(europcarInitialItem?.booking_setting?.customer_email) && (
            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {t("transport.guestEmailLabel")}{" "}
                  {europcarInitialItem?.booking_setting?.customer_email === "mandatory" && "*"}
                </Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"emailForm"}
                  type="string"
                  value={dataForBooking?.emailForm}
                  onChange={event => handleBookingFormInputChange(event, "emailForm")}
                  error={errorField?.emailForm}
                  dataAttribute="europcar-driver-email-input"
                />
              </Grid>
            </Grid>
          )}
          {isVisibleField(europcarInitialItem?.booking_setting?.customer_phone) && (
            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {t("transport.guestPhoneLabel")}{" "}
                  {europcarInitialItem?.booking_setting?.customer_phone === "mandatory" && "*"}
                </Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"mobileNumberForm"}
                  type="string"
                  value={dataForBooking?.mobileNumberForm}
                  onChange={event => handleBookingFormInputChange(event, "mobileNumberForm")}
                  error={errorField?.mobileNumberForm}
                  dataAttribute="europcar-driver-phone-input"
                />
                <Box sx={{ fontSize: { xs: 10, sm: 12 }, mt: 1 }}>{t("phoneNumberLabel")}</Box>
              </Grid>
            </Grid>
          )}
          {isVisibleField(europcarInitialItem?.booking_setting?.comment) && (
            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {t("transport.commentLabel")} {europcarInitialItem?.booking_setting?.comment === "mandatory" && "*"}
                </Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"commentForm"}
                  type="string"
                  value={dataForBooking?.commentForm}
                  onChange={event => handleBookingFormInputChange(event, "commentForm")}
                  error={errorField?.commentForm}
                  dataAttribute="europcar-driver-comment-input"
                />
              </Grid>
            </Grid>
          )}
          {europcarInitialItem?.terms_and_conditions_url && (
            <Grid item xs={12} mb={2} sx={{ flexDirection: "column", display: "flex", justifyContent: "center" }}>
              <Box sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Checkbox
                  sx={{
                    "&&.PrivateSwitchBase-root": {
                      paddingLeft: 0
                    }
                  }}
                  checked={dataForBooking?.acceptTerms}
                  onChange={event => handleBookingFormInputChange(event, "acceptTerms")}
                  data-cy="europcar-accept-terms-checkbox"
                />
                <Box>
                  {t("transport.acceptEuropcarPrivacyPolicy")}
                  <Link
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{ fontSize: { xs: 12, sm: 14 }, color: "#76a8cf", marginLeft: 1, textDecoration: "underline" }}
                    href={europcarInitialItem?.terms_and_conditions_url}
                  >
                    {t("viewLabel")}
                  </Link>
                </Box>
              </Box>

              {errorField?.acceptTerms && (
                <Box mt={1} className="uber__error-msg">
                  {t("transport.acceptTermsError")}
                </Box>
              )}
            </Grid>
          )}
        </Grid>

        <Grid container width={{ xs: "100%", sm: "100%", md: "50%", justifyContent: "center" }}>
          {selectedCar ? (
            <Box sx={{ alignItems: { xs: "left", md: "center" }, display: "flex", flexDirection: "column", flex: 1 }}>
              <Box pb={2} sx={{ display: { xs: "flex", md: "none" } }}>
                <strong style={{ fontSize: 18 }}>{t("transport.selectedVehicleInfoTitle")}</strong>
              </Box>

              <strong style={{ fontSize: 19 }}>{selectedCar?.sample}</strong>
              <Box sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
                {selectedCar?.transmission} • {selectedCar?.fuel_type}
              </Box>
              {/* PRICE */}
              <Box mt={1} sx={{ fontSize: 20, fontFamily: "Neutraface2Demi" }}>
                {t("transport.basicRateLabel", {
                  currency: selectedCar?.currency,
                  price: selectedCar?.final_price.toFixed(2)
                })}
              </Box>
              <Box sx={{ maxWidth: { xs: "100%", sm: "100%", md: "85%" }, width: "100%" }}>
                <EuropcarWhatsIncludedSection selectedCar={selectedCar} />
              </Box>
              {/* IMAGE */}
              <Box sx={{ width: { xs: 135, sm: 140, lg: 160 }, flexShrink: 0 }} pt={1}>
                <img
                  src={selectedCar?.image_url}
                  alt={selectedCar?.sample}
                  style={{ width: "100%", objectFit: "contain" }}
                />
              </Box>

              {/* Insurance section */}
              <Box sx={{ maxWidth: { xs: "100%", sm: "100%", md: "85%" } }}>
                <EuropcarInsuranceSection selectedCar={selectedCar} />
              </Box>
            </Box>
          ) : (
            <Box mt={4}>{t("transport.noVehicleSelectedMessage")}</Box>
          )}
        </Grid>
      </Box>
    </Box>
  );
}
